import { client } from '../main.js';

/**
 * Absolute URL for a feed's public path.
 *
 * The API returns `feedPath` as a path only, because the origin it should
 * resolve against depends on where the manager is talking to the API —
 * dev proxy, staging, or production. Shared between the feed editor and
 * the marketing list so the URL a manager copies from the row is byte-
 * identical to the one shown on the editor page.
 */
export function resolveFeedUrl(feedPath: string): string {
  try {
    // baseUrl is '' when the API is same-origin behind the dev proxy.
    return new URL(
      feedPath,
      client.baseUrl || window.location.origin,
    ).toString();
  } catch {
    return feedPath;
  }
}

/**
 * Deep link to a catalog in Meta's Commerce Manager.
 *
 * `business_id` is not decoration: a user who belongs to several business
 * portfolios lands in whichever context Meta last used, and a catalog
 * they can't see in that context reads as "catalog missing". Passing it
 * pins the right portfolio.
 *
 * Meta doesn't document this path, so it lives in one function — if they
 * move it, this is the only line to change.
 */
export function commerceManagerUrl(
  catalogId: string,
  businessId?: string | null,
): string {
  const url = new URL(
    `https://business.facebook.com/commerce/catalogs/${catalogId}/products`,
  );
  if (businessId) url.searchParams.set('business_id', businessId);
  return url.toString();
}

/**
 * Copy text to the clipboard, reporting whether it landed.
 *
 * `navigator.clipboard` is unavailable outside a secure context (plain
 * http on a LAN address, which is how people demo this), so the caller
 * has to be able to tell the user it failed rather than silently doing
 * nothing and looking broken.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
